import React from "react";
import { useTranslation } from "react-i18next";
import type { TransformedCartItem } from "../../../../cart/types/cartApiTypes";

interface MiniCartItemRowProps {
  item: TransformedCartItem;
  currencySymbol: string;
  onRemoveItem: (key: string) => void;
  onQuantityChange: (key: string, newQuantity: number) => void; // Add quantity change handler prop
  isProcessing: boolean; // Combined processing state
}

const MiniCartItemRow: React.FC<MiniCartItemRowProps> = ({
  item,
  currencySymbol,
  onRemoveItem,
  onQuantityChange, // Receive handler
  isProcessing, // Receive combined state
}) => {
  const { t } = useTranslation();
  const thumbnailUrl = item.images?.[0]?.thumbnail || "";

  // Quantity limits and checks
  const min = item.quantityLimits.minimum;
  const max = item.quantityLimits.maximum;
  const step = item.quantityLimits.multiple_of;
  const canDecrease = item.quantity > min;
  const canIncrease = max <= 0 || item.quantity < max;

  return (
    <div className="mini-cart-item-row">
      <div className="mini-cart-item-row__thumbnail">
        {thumbnailUrl ? (
          <a href={item.permalink}>
            <img src={thumbnailUrl} alt={item.name} />
          </a>
        ) : (
          <div className="thumbnail-placeholder"></div>
        )}
      </div>
      <div className="mini-cart-item-row__details">
        <a href={item.permalink} className="mini-cart-item-row__name">
          {item.name}
        </a>
        {/* Display variation attributes */}
        {item.attributes.length > 0 && (
          <div className="mini-cart-item-row__attributes">
            {item.attributes.map((attribute) => {
              const term = attribute.term;
              const displayType = attribute.displayType || "button-text";

              return (
                <div
                  key={attribute.id}
                  className={`mini-cart-item-row__attribute type-${displayType}`}
                >
                  <span className="mini-cart-item-row__attribute-name">
                    {attribute.name}:
                  </span>
                  {(() => {
                    switch (displayType) {
                      case "color":
                        return (
                          <span
                            className="mini-cart-item-row__color-swatch"
                            style={{
                              backgroundColor: term.color || "#CCCCCC",
                            }}
                            title={term.name}
                          ></span>
                        );
                      case "button-text":
                        return (
                          <span className="mini-cart-item-row__text-button">
                            {term.name}
                          </span>
                        );
                      case "button-image":
                        return term.buttonImage?.thumbnail ? (
                          <img
                            src={term.buttonImage.thumbnail}
                            alt={term.name}
                            className="mini-cart-item-row__image-preview"
                            title={term.name}
                          />
                        ) : (
                          <span className="mini-cart-item-row__text-button">
                            {term.name}
                          </span> // Fallback
                        );
                      default:
                        return null; // Return null for default case
                    }
                  })()}
                </div>
              );
            })}
          </div>
        )}

        {/* Quantity Controls */}
        <div className="mini-cart-item-row__quantity">
          <button
            className="quantity-button decrease"
            onClick={() => onQuantityChange(item.key, item.quantity - step)}
            disabled={!canDecrease || isProcessing}
            aria-label={t("cart.item.decreaseQuantity", "Decrease quantity")}
          >
            -
          </button>
          <span className="quantity-display">{item.quantity}</span>
          <button
            className="quantity-button increase"
            onClick={() => onQuantityChange(item.key, item.quantity + step)}
            disabled={!canIncrease || isProcessing}
            aria-label={t("cart.item.increaseQuantity", "Increase quantity")}
          >
            +
          </button>
        </div>

        {/* Price (Optional: Can show line total or unit price) */}
        <div className="mini-cart-item-row__price">
          {/* Example: Show line subtotal */}
          {item.totals.formattedLineSubtotal}
          {currencySymbol}
          {/* Or show unit price: {item.formattedPrice}{currencySymbol} */}
        </div>
      </div>
      <button
        className="mini-cart-item-row__remove-button"
        onClick={() => onRemoveItem(item.key)}
        disabled={isProcessing} // Use combined state
        aria-label={t("cart.item.remove", "Remove item")}
      >
        &times; {/* Simple 'x' icon */}
      </button>
    </div>
  );
};

export default MiniCartItemRow;
